<template>
  <div class="content mb-5">
    <b-row>
      <b-col class="mb-3">
        <h2>{{ this.$t("User Details") }}</h2>
      </b-col>
    </b-row>
    <b-form @submit="onSubmit" class="mb-5" v-if="!loading">
      <b-row>
        <b-col cols="12" md="6">
          <b-form-group
            label-for="account-name"
            :label="this.$t('Account Name')"
            :state="$validateState($v.form.accountname)"
          >
            <b-form-input
              id="account-name"
              v-model="form.accountname"
              @blur="setValidationInput('accountname', $event.target.value)"
              :state="$validateState($v.form.accountname)"
              :placeholder="this.$t('e.g. Marketing')"
            />

            <template #invalid-feedback>
              <p v-if="!$v.form.accountname.required">
                {{ $t("This is a required field.") }}
              </p>
            </template>
          </b-form-group>

          <b-form-group
            label-for="description"
            :label="this.$t('Description')"
            :state="$validateState($v.form.description)"
          >
            <b-form-input
              id="description"
              v-model="form.description"
              @blur="setValidationInput('description', $event.target.value)"
              :state="$validateState($v.form.description)"
              :placeholder="this.$t('e.g. Marketing')"
            />

            <template #invalid-feedback>
              <p v-if="!$v.form.description.required">
                {{ $t("This is a required field.") }}
              </p>
            </template>
          </b-form-group>

          <b-form-group
            :label="this.$t('License')"
            label-for="license"
            :state="$validateState($v.form.license)"
          >
            <b-form-input
              data-test-id="modal-customer-license"
              :state="$validateState($v.form.license)"
              id="license"
              v-model="form.license"
              @blur="setValidationInput('license', $event.target.value)"
              :placeholder="$t('License')"
            />

            <template #invalid-feedback>
              <p v-if="!$v.form.license.required">
                {{ $t("This is a required field.") }}
              </p>
              <p v-if="$v.form.license.required && !$v.form.license.maxLength">
                {{
                  $t("X must be at most X characters long.", {
                    field: $t("License"),
                    length: 64,
                  })
                }}
              </p>
            </template>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group
            label-for="email"
            :label="this.$t('Email')"
            :state="$validateState($v.form.email)"
          >
            <b-form-input
              id="email"
              v-model="form.email"
              @blur="setValidationInput('email', $event.target.value)"
              :state="$validateState($v.form.email)"
              :placeholder="this.$t('e.g. Marketing')"
            />

            <template #invalid-feedback>
              <p v-if="!$v.form.email.required">
                {{ $t("This is a required field.") }}
              </p>
            </template>
          </b-form-group>

          <b-form-group :label="this.$t('Timezone')" label-for="timezone">
            <b-form-select
              id="timezone"
              v-model="$v.form.timezone.$model"
              :state="$validateState($v.form.timezone)"
              :options="this.$timezones"
            ></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>
      <t-save-button
        data-test-id="overview-user-save"
        :form-model="$v.form"
        :fetching="fetching"
        @submit="onSubmit"
      />
    </b-form>

    <b-row v-if="loading" class="mb-5">
      <b-col cols="12" md="6">
        <b-skeleton animation="wave" width="85%"></b-skeleton>
        <b-skeleton animation="wave" width="55%"></b-skeleton>
        <b-skeleton animation="wave" width="70%"></b-skeleton>
      </b-col>
      <b-col cols="12" md="6">
        <b-skeleton animation="wave" width="85%"></b-skeleton>
        <b-skeleton animation="wave" width="55%"></b-skeleton>
        <b-skeleton animation="wave" width="70%"></b-skeleton>
      </b-col>
    </b-row>
  </div>
</template>

<route>
{
  "name": "System Customer"
}
</route>

<script>
import { mapActions, mapGetters } from "vuex";
import { required, maxLength, email } from "vuelidate/lib/validators";

export default {
  layout: "default",
  data() {
    return {
      loading: true,
      fetching: false,
      selected: {
        policy: null,
        status: 0,
      },
      form: {
        accountname: null,
        email: null,
        description: null,
        timezone: null,
        license: null,
      },
    };
  },
  validations: {
    form: {
      accountname: {
        required,
      },
      license: {
        required,
        maxLength: maxLength(64),
      },
      description: {
        required,
      },
      email: {
        required,
        email,
      },
      timezone: {},
    },
  },
  methods: {
    ...mapActions("users", ["fetchCustomerAccount", "updateCustomerAccount"]),
    ...mapActions("pageMeta", ["setPageTitle"]),
    ...mapActions("toast", ["displayToast"]),
    setValidationInput(object, value) {
      this.form[`${object}`] = value;
      this.$v.form[`${object}`].$touch();
    },
    async onSubmit() {
      this.fetching = true;

      try {
        if (!this.form.timezone) delete this.form.timezone;
        await this.updateCustomerAccount({
          id: this.selectedUserAccount.id,
          customer: this.form,
        });
        this.displayToast({
          title: this.$t("Success"),
          message: "Updated",
          duration: 2000,
          variant: "success",
        });
      } finally {
        this.fetching = false;
        this.$v.form.$reset();
      }
    },
  },
  computed: {
    ...mapGetters("users", ["selectedUserAccount"]),
    ...mapGetters("authentication", ["selectedUser"]),
  },
  watch: {
    selectedUserAccount: {
      handler() {
        this.setPageTitle(this.selectedUserAccount.accountname);
        this.form = { ...this.selectedUserAccount };
      },
      deep: true,
    },
  },
  async created() {
    await this.fetchCustomerAccount({
      id: this.$route.params.id,
    });
    this.loading = false;
  },
};
</script>
